import React, { useState, useEffect } from 'react';
import { alertaGeneral } from '../../utils/alertas';
import Contenedor from '../GlobalComponents/Contenedor';
import TableDocuments from './TableDocuments';
import { ReactComponent as Perfil } from "../../assets/icons/perfilUsuario.svg";
import apiClient from '../../utils/axiosPetition';

const ReviewProfile = ({ doctor, onGoBack, fetchDoctors }) => {
    const [updatedDoctor, setUpdatedDoctor] = useState(null);
    const [confirmingEpayco, setConfirmingEpayco] = useState(false);
    useEffect(() => {
        // Definir todos los posibles documentos con sus URLs
        const allDocumentsTemplate = [
            { document: 'foto_perfil', url: doctor.urlProfessionalPhoto },
            { document: 'cedulaPDF', url: doctor.urlIdDocument },
            { document: 'diploma_acta_grado', url: doctor.urlUndergraduateDiploma },
            { document: 'diploma_especializacion', url: doctor.urlSpecializationDiploma },
            { document: 'homologacion_estudios', url: doctor.urlStudyHomologation },
            { document: 'tarjeta_profesional', url: doctor.urlProfessionalCard },
            { document: 'firma_profesional', url: doctor.urlProfessionalSignature },
            { document: 'rethus', url: doctor.urlRethusDocument },
            { document: 'poliza_responsabilidad_civil', url: doctor.urlCivilLiabilityPolicyDocument },
        ].filter(doc => doc.url && doc.url !== '');

        // Si hay documentos aprobados, usarlos como base y completar con los que faltan
        if (doctor.documentsApproved && doctor.documentsApproved.length > 0) {
            // Crear un mapa de documentos existentes por su campo 'document'
            const existingDocsMap = new Map();
            doctor.documentsApproved.forEach(doc => {
                existingDocsMap.set(doc.document, doc);
            });

            // Mapear todos los documentos: usar los existentes o crear nuevos
            const mappedDocuments = allDocumentsTemplate.map(templateDoc => {
                const existingDoc = existingDocsMap.get(templateDoc.document);
                if (existingDoc) {
                    // Si existe, usar el documento existente con su URL actualizada
                    // Si está rechazado pero no tiene observaciones válidas, resetear a null
                    const hasValidRejection = existingDoc.isApproved === false && 
                                            existingDoc.rejectionReason && 
                                            existingDoc.rejectionReason.trim() !== '';
                    
                    // Si está rechazado sin observaciones válidas, resetear a estado neutro
                    if (existingDoc.isApproved === false && !hasValidRejection) {
                        return {
                            document: templateDoc.document,
                            url: templateDoc.url,
                            isApproved: null,
                            rejectionReason: '',
                        };
                    }
                    
                    // Mantener el estado solo si está aprobado o rechazado con observaciones válidas
                    return {
                        ...existingDoc,
                        url: templateDoc.url,
                    };
                } else {
                    // Si no existe, crear un nuevo documento con estado null (no evaluado)
                    return {
                        document: templateDoc.document,
                        url: templateDoc.url,
                        isApproved: null,
                        rejectionReason: '',
                    };
                }
            });
            setUpdatedDoctor({ ...doctor, documentsApproved: mappedDocuments });
        } else {
            // Si no hay documentos aprobados, crear todos desde cero
            const mappedDocuments = allDocumentsTemplate.map(templateDoc => ({
                document: templateDoc.document,
                url: templateDoc.url,
                isApproved: null,
                rejectionReason: '',
            }));
            setUpdatedDoctor({ ...doctor, documentsApproved: mappedDocuments });
        }
    }, [doctor]);

    const handleConfirmarEpayco = async () => {
        const id = (updatedDoctor?.epaycoAliadoId || doctor?.epaycoAliadoId || '').trim();
        if (!id) {
            alertaGeneral({ titulo: 'Falta ID de ePayco', messageHtml: 'El profesional aún no ha reportado su Account ID desde Configuración de citas.', isError: true });
            return;
        }
        setConfirmingEpayco(true);
        try {
            const res = await apiClient.put(`/api/epayco/doctor/${updatedDoctor._id}/epayco-id`, { epaycoAliadoId: id });
            if (res.data?.message) {
                alertaGeneral({ titulo: 'Profesional aprobado', messageHtml: 'El registro en ePayco se confirmó correctamente. El profesional ha quedado <strong>aprobado</strong> y ya puede recibir pagos de citas.', isInfo: true });
                fetchDoctors();
                onGoBack();
            }
        } catch (err) {
            alertaGeneral({ titulo: 'Error', messageHtml: err?.response?.data?.message || 'No se pudo confirmar el ID de ePayco.', isError: true });
        } finally {
            setConfirmingEpayco(false);
        }
    };

    const handleDocumentChange = (index, action, reason = '') => {
        const updatedDocuments = [...updatedDoctor.documentsApproved];
        if (action === 'approved') {
            updatedDocuments[index].isApproved = true;
            updatedDocuments[index].rejectionReason = '';
        } else if (action === 'rejected') {
            updatedDocuments[index].isApproved = false;
            updatedDocuments[index].rejectionReason = reason;
        } else if (action === '') {
            // Si se limpia el select (seleccionar "Seleccionar"), volver a null (no evaluado)
            updatedDocuments[index].isApproved = null;
            updatedDocuments[index].rejectionReason = '';
        }
        // Si reason se pasa pero no hay action, es porque se está editando el motivo de rechazo
        // En ese caso, mantener el estado actual pero actualizar el motivo
        setUpdatedDoctor({ ...updatedDoctor, documentsApproved: updatedDocuments });
    };

    const handleSaveChanges = async () => {
        // Validar que si hay documentos rechazados, tengan observaciones obligatorias
        const documentosRechazados = updatedDoctor.documentsApproved.filter(
            doc => doc.isApproved === false
        );
        
        const documentosSinObservaciones = documentosRechazados.filter(
            doc => !doc.rejectionReason || doc.rejectionReason.trim() === ''
        );
        
        if (documentosSinObservaciones.length > 0) {
            const nombresDocumentos = documentosSinObservaciones.map(
                doc => doc.document?.replace(/_/g, ' ')
            ).join(', ');
            
            alertaGeneral({
                titulo: 'Observaciones Requeridas',
                messageHtml: `Debes agregar observaciones (motivo de rechazo) para los siguientes documentos: ${nombresDocumentos}`,
                isError: true,
            });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/clinic/updateDoctorDocuments/${updatedDoctor._id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        documentsApproved: updatedDoctor.documentsApproved,
                    }),
                }
            );
            if (response.ok) {
                const data = await response.json();
                const allApproved = updatedDoctor.documentsApproved.every(d => d.isApproved === true);
                if (allApproved) {
                    // Mostrar bloque ePayco en esta misma pantalla: forzar estado a pendiente_epayco
                    setUpdatedDoctor(prev => ({ ...prev, estado: 'pendiente_epayco' }));
                    fetchDoctors();
                    alertaGeneral({
                        titulo: 'Documentos aprobados',
                        messageHtml: 'Los cambios se guardaron. Complete los pasos que se muestran abajo para registrar al profesional en ePayco y dejarlo aprobado.',
                        isInfo: true,
                    });
                    // No hacer onGoBack: el administrador se queda en la pantalla y ve el bloque de pasos ePayco
                } else {
                    alertaGeneral({
                        titulo: 'Cambios Guardados',
                        messageHtml: 'Los cambios se guardaron correctamente.',
                    });
                    fetchDoctors();
                    onGoBack();
                }
            } else {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'No se pudieron guardar los cambios.',
                    isError: true,
                });
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            alertaGeneral({
                titulo: 'Error',
                messageHtml: 'Ocurrió un error al guardar los cambios.',
                isError: true,
            });
        }
    };

    const handleViewDocument = (url) => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: 'El documento no tiene una URL válida.',
                isError: true
            });
        }
    };

    if (!updatedDoctor) return <div>Cargando...</div>;

    const doctorDisplay = updatedDoctor ?? doctor;
    const isPendienteEpayco = (doctorDisplay?.estado === 'pendiente_epayco');
    const isAprobadoSinEpayco = (doctorDisplay?.estado === 'aprobado' && !doctorDisplay?.epaycoAliadoId);

    return (
        <Contenedor title="Revisión de Perfil y Documentos" icon={Perfil}>
                {/* Bloque: pasos ePayco (estado pendiente_epayco) — visible en la misma pantalla, sin modal */}
            {isPendienteEpayco && (
                <div className="mb-8 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 overflow-hidden shadow-sm">
                    <div className="px-5 py-4 border-b border-blue-200 bg-white/60">
                        <h3 className="text-lg font-semibold text-[#205087]">Siguiente paso: registrar en ePayco</h3>
                        <p className="text-sm text-gray-600 mt-1">Los documentos están aprobados. Complete estos pasos para que el profesional quede aprobado y pueda recibir pagos.</p>
                    </div>
                    <div className="p-5">
                        <ol className="space-y-4 mb-6">
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1f94c2ff] text-white font-semibold flex items-center justify-center text-sm">1</span>
                                <div>
                                    <span className="font-medium text-gray-800">Ir a ePayco</span>
                                    <p className="text-sm text-gray-600 mt-0.5">Dashboard de ePayco → <strong>Pagos Divididos</strong> → <strong>Split Payments</strong> → <strong>Receptores</strong> → <strong>Nuevos Usuarios</strong>.</p>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1f94c2ff] text-white font-semibold flex items-center justify-center text-sm">2</span>
                                <div>
                                    <span className="font-medium text-gray-800">Registrar al profesional</span>
                                    <p className="text-sm text-gray-600 mt-0.5">Registre al profesional en ePayco usando el <strong>correo del doctor</strong> (el mismo correo con el que está en la plataforma).</p>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1f94c2ff] text-white font-semibold flex items-center justify-center text-sm">3</span>
                                <div>
                                    <span className="font-medium text-gray-800">Confirmar ID reportado por el profesional</span>
                                    <p className="text-sm text-gray-600 mt-0.5">El doctor registra su Account ID en Configuración de citas. Verifica el ID abajo y pulse <strong>Confirmar registro en ePayco</strong> para aprobarlo.</p>
                                </div>
                            </li>
                        </ol>
                        <div className="space-y-4 p-4 rounded-lg bg-white border border-blue-100">
                            {/* Correo del doctor: solo lectura y copiable */}
                            <div>
                                <label className="form-label block text-sm font-medium text-gray-800 mb-1">Correo del doctor (usado en ePayco)</label>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <input
                                        type="text"
                                        readOnly
                                        value={updatedDoctor?.email || ''}
                                        className="form-input flex-1 min-w-[200px] bg-gray-50 font-mono text-gray-800"
                                        onClick={(e) => e.target.select()}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const email = updatedDoctor?.email || '';
                                            if (email) {
                                                navigator.clipboard.writeText(email);
                                                alertaGeneral({ titulo: 'Copiado', messageHtml: 'El correo se copió al portapapeles.', isInfo: true });
                                            }
                                        }}
                                        className="px-3 py-2 border border-[#1f94c2ff] text-[#1f94c2ff] font-medium rounded-lg hover:bg-[#1f94c2ff] hover:text-white transition-colors"
                                    >
                                        Copiar
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="form-label block text-sm font-medium text-gray-800 mb-1">
                                    Account ID ePayco reportado por el profesional
                                    {(doctorDisplay?.epaycoPendienteConfirmacionAdmin || updatedDoctor?.epaycoPendienteConfirmacionAdmin) && (
                                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                            ID pendiente confirmar
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="text"
                                    readOnly
                                    value={(updatedDoctor?.epaycoAliadoId || doctor?.epaycoAliadoId || '').trim() || 'El profesional aún no ha reportado su ID'}
                                    className={`form-input w-full font-mono ${(updatedDoctor?.epaycoAliadoId || doctor?.epaycoAliadoId) ? 'bg-gray-50 text-gray-800' : 'bg-amber-50 text-amber-800 italic'}`}
                                />
                                {!(updatedDoctor?.epaycoAliadoId || doctor?.epaycoAliadoId) && (
                                    <p className="text-xs text-amber-700 mt-1">El profesional debe guardar su ID desde Configuración de citas antes de confirmar.</p>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleConfirmarEpayco}
                                    disabled={confirmingEpayco}
                                    className="px-4 py-2 bg-[#1f94c2ff] text-white font-semibold rounded-lg hover:bg-[#205087ff] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {confirmingEpayco ? 'Guardando...' : 'Confirmar registro en ePayco'}
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">Al confirmar, el profesional pasará a <strong>Aprobado</strong> y podrá recibir pagos de citas.</p>
                    </div>
                </div>
            )}
                {/* Aviso: profesional aprobado pero sin ID ePayco (por si se eliminó después) */}
            {isAprobadoSinEpayco && (
                <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-left">
                    <p className="font-semibold text-amber-800 mb-2">Correo ePayco no registrado</p>
                    <p className="text-sm text-amber-800">Este profesional está aprobado pero no tiene registrado el correo usado en ePayco. Regístrelo en ePayco (con el mismo correo del doctor) y confírmelo aquí si desea que reciba pagos de citas.</p>
                </div>
            )}
                {/* Datos del Perfil */}
            <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-[#205087ff]">Datos del Perfil</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                        <label className="form-label">Nombre Completo</label>
                            <input
                                type="text"
                                value={updatedDoctor.fullName}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                        </div>
                        <div>
                        <label className="form-label">Correo Electrónico</label>
                            <input
                                type="email"
                                value={updatedDoctor.email}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                        </div>
                        <div>
                        <label className="form-label">Número de Teléfono</label>
                            <input
                                type="tel"
                                value={updatedDoctor.cellNumber}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                        </div>
                        <div>
                        <label className="form-label">Tipo de ID</label>
                            <input
                                type="text"
                                value={updatedDoctor.idType}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                        </div>
                        <div>
                        <label className="form-label">Número de ID</label>
                            <input
                                type="text"
                                value={updatedDoctor.idNumber}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                        </div>
                        <div>
                        <label className="form-label">Servicio</label>
                            <input
                                type="text"
                                value={updatedDoctor.service}
                                readOnly
                            className="form-input bg-gray-100"
                            />
                    </div>
                </div>
            </div>

            {/* Sección de Precios para Especialidades que requieren configuración */}
            {(() => {
                // Función para normalizar el servicio
                const normalizeService = (service) => {
                    if (!service) return '';
                    return service.toLowerCase()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
                        .replace(/\d+/g, '') // Quitar números
                        .replace(/_/g, ' ') // Reemplazar guiones bajos
                        .replace(/-/g, ' ') // Reemplazar guiones
                        .trim();
                }

                // Función para obtener la configuración de precios según la especialidad
                const getPreciosConfig = () => {
                    if (!updatedDoctor || !updatedDoctor.service) return null;
                    
                    const serviceNormalized = normalizeService(updatedDoctor.service);
                    
                    // Configuración de precios por especialidad (solo para verificación, no se usan los valores)
                    const preciosConfig = {
                        'medicina familiar': true,
                        'medicina general': true,
                        'terapia fisica': true,
                        'terapia de lenguaje': true,
                        'terapia lenguaje': true,
                        'fonoaudiologia': true,
                        'terapia ocupacional': true,
                        'terapeuta ocupacional': true,
                        'terapia respiratoria': true,
                        'terapeuta respiratorio': true,
                        'psicologia': true,
                        'psicologo': true,
                        'trabajo social': true,
                        'nutricion': true,
                        'nutricionista': true,
                        'enfermeria': true,
                        'enfermero': true,
                        'enfermera': true
                    };

                    // Buscar coincidencia en la configuración
                    for (const [key] of Object.entries(preciosConfig)) {
                        if (serviceNormalized.includes(key)) {
                            return true;
                        }
                    }

                    return null;
                }

                // Función para verificar si la especialidad requiere configuración de precios
                const requiereConfiguracionPrecios = () => {
                    return getPreciosConfig() !== null;
                }

                const requierePrecios = requiereConfiguracionPrecios();
                const tienePrecios = updatedDoctor?.preciosCitas && (
                    updatedDoctor.preciosCitas.teleconsultaPrimeraVez ||
                    updatedDoctor.preciosCitas.precioControles ||
                    updatedDoctor.preciosCitas.precioTeleorientacion
                );

                return requierePrecios && tienePrecios ? (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-4 text-[#205087ff]">Precios de Citas Configurados</h2>
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-4">
                            <p className="text-sm text-blue-700">
                                <strong>Nota:</strong> Estos son los precios que el profesional ha configurado para sus servicios.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="form-label">Precio Teleconsulta Primera Vez</label>
                                <input
                                    type="text"
                                    value={updatedDoctor.preciosCitas?.teleconsultaPrimeraVez 
                                        ? `$${updatedDoctor.preciosCitas.teleconsultaPrimeraVez.toLocaleString('es-CO')}` 
                                        : 'No configurado'}
                                    readOnly
                                    className="form-input bg-gray-100 font-semibold text-green-700"
                                />
                            </div>
                            <div>
                                <label className="form-label">Precio Citas de Control</label>
                                <input
                                    type="text"
                                    value={updatedDoctor.preciosCitas?.precioControles 
                                        ? `$${updatedDoctor.preciosCitas.precioControles.toLocaleString('es-CO')}` 
                                        : 'No configurado'}
                                    readOnly
                                    className="form-input bg-gray-100 font-semibold text-green-700"
                                />
                            </div>
                            <div>
                                <label className="form-label">Precio Teleorientación</label>
                                <input
                                    type="text"
                                    value={updatedDoctor.preciosCitas?.precioTeleorientacion 
                                        ? `$${updatedDoctor.preciosCitas.precioTeleorientacion.toLocaleString('es-CO')}` 
                                        : 'No configurado'}
                                    readOnly
                                    className="form-input bg-gray-100 font-semibold text-green-700"
                                />
                            </div>
                        </div>
                    </div>
                ) : null;
            })()}

                {/* Contrato de vinculación firmado - Plan Miembro Free */}
            {updatedDoctor.urlContratoVinculacionFirmado && updatedDoctor.urlContratoVinculacionFirmado !== '' && (
                <div className="mb-6 p-4 bg-blue-50 border border-[#1f94c2ff] rounded-lg">
                    <h2 className="text-lg font-semibold mb-2 text-[#205087ff]">Contrato de vinculación - Plan Miembro Free</h2>
                    <p className="text-sm text-gray-700 mb-2">
                        Firmado el {updatedDoctor.fechaFirmaContratoVinculacion
                            ? new Date(updatedDoctor.fechaFirmaContratoVinculacion).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—'}
                    </p>
                    <a
                        href={updatedDoctor.urlContratoVinculacionFirmado}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-[#1f94c2ff] text-white rounded-md hover:bg-[#205087ff] text-sm"
                    >
                        Ver PDF firmado
                    </a>
                </div>
            )}
            {updatedDoctor.urlContratoVinculacionPremiumFirmado && updatedDoctor.urlContratoVinculacionPremiumFirmado !== '' && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                    <h2 className="text-lg font-semibold mb-2 text-amber-800">Contrato de vinculación - Plan Miembro Premium</h2>
                    <p className="text-sm text-gray-700 mb-2">
                        Firmado el {updatedDoctor.fechaFirmaContratoVinculacionPremium
                            ? new Date(updatedDoctor.fechaFirmaContratoVinculacionPremium).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—'}
                    </p>
                    <a
                        href={updatedDoctor.urlContratoVinculacionPremiumFirmado}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm"
                    >
                        Ver PDF firmado
                    </a>
                </div>
            )}

                {/* Documentos */}
            <div className="mb-8 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                    <h2 className="text-lg font-semibold text-slate-800">Revisión de documentos</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Revise cada documento, visualícelo y apruebe o rechace con observaciones.</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="bg-slate-100 border-b border-slate-200">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Documento</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-24">Ver</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-40">Decisión</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[200px]">Observaciones (si rechaza)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {updatedDoctor.documentsApproved.map((doc, index) => {
                                const status = doc.isApproved === true ? 'approved' : (doc.isApproved === false && doc.rejectionReason?.trim()) ? 'rejected' : '';
                                return (
                                    <tr key={index} className="hover:bg-slate-50/80 transition-colors">
                                        <td className="px-4 py-3">
                                            <span className="font-medium text-slate-800 capitalize">{doc.document?.replace(/_/g, ' ')}</span>
                                            {status && (
                                                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                    status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                onClick={() => handleViewDocument(doc.url)}
                                                className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium bg-[#1f94c2] text-white hover:bg-[#205087] transition-colors"
                                            >
                                                Ver
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={status}
                                                onChange={e => handleDocumentChange(index, e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2]"
                                            >
                                                <option value="">Seleccionar</option>
                                                <option value="approved">Aprobar</option>
                                                <option value="rejected">Rechazar</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.isApproved === false && (
                                                <textarea
                                                    placeholder="Indique el motivo del rechazo (obligatorio)"
                                                    value={doc.rejectionReason || ''}
                                                    onChange={e => handleDocumentChange(index, 'rejected', e.target.value)}
                                                    rows={2}
                                                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2] resize-none ${
                                                        !doc.rejectionReason || doc.rejectionReason.trim() === '' ? 'border-red-300 bg-red-50/50' : 'border-slate-200'
                                                    }`}
                                                    required
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

                <div className="flex justify-end mt-6">
                    <button
                        onClick={handleSaveChanges}
                    className="px-5 py-3 bg-[#1f94c2ff] text-white font-semibold rounded-md shadow-md hover:bg-[#205087ff]"
                    >
                        Guardar cambios
                    </button>
                <button
                    onClick={onGoBack}
                    className="ml-4 px-5 py-3 bg-gray-400 text-white font-semibold rounded-md shadow-md hover:bg-gray-600"
                >
                    Volver
                </button>
                </div>
        </Contenedor>
    );
};

export default ReviewProfile;